import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { Box } from '@material-ui/core';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IAgencyTalentPermissionPayload, IProfileMedia } from 'shared/interfaces/IProfile';
import ImageItem from './Items/ImageItem';

interface SortableImagesProps {
  sensors: any;
  images: IProfileMedia[];
  idToEdit: string;
  mediaPatching: boolean;
  activeId: string | null;
  onDragStart: (event: DragStartEvent) => void;
  onDragOver: (event: DragEndEvent) => void;
  onDragEnd: () => void;
  onEdit: (media: any) => void;
  handleClick: (idx: number) => void;
  handleCheck: (image: any) => void;
  handleAgencyTalentPermissions: (payload: IAgencyTalentPermissionPayload) => void;
}

// Export a ref interface to allow parent to control the component
export interface SortableImagesRef {
  resetDragState: () => void;
}

function propsAreEqual(prevProps: SortableImagesProps, nextProps: SortableImagesProps) {
  // Only re-render when specific props change
  return (
    prevProps.images === nextProps.images &&
    prevProps.activeId === nextProps.activeId &&
    prevProps.idToEdit === nextProps.idToEdit &&
    prevProps.mediaPatching === nextProps.mediaPatching
  );
}

// Use forwardRef to allow parent to access internal methods
const SortableImages = memo(
  forwardRef<SortableImagesRef, SortableImagesProps>(
    (
      {
        sensors,
        images,
        idToEdit,
        mediaPatching,
        activeId,
        onDragStart,
        onDragOver,
        onDragEnd,
        onEdit,
        handleClick,
        handleCheck,
        handleAgencyTalentPermissions,
      },
      ref,
    ) => {
      // Use a ref to track if the component is in a dragging state
      const isDraggingRef = useRef(false);
      const [internalActiveId, setInternalActiveId] = useState<string | null>(null);

      // Sync with parent's activeId
      useEffect(() => {
        setInternalActiveId(activeId);
      }, [activeId]);

      // Expose methods to parent component
      useImperativeHandle(ref, () => ({
        resetDragState: () => {
          isDraggingRef.current = false;
          setInternalActiveId(null);
        },
      }));

      // Stabilize event handlers to avoid unnecessary rerenders
      const handleDragStart = useCallback(
        (event: DragStartEvent) => {
          if (isDraggingRef.current) return;
          isDraggingRef.current = true;
          setInternalActiveId(event.active.id as string);
          onDragStart(event);
        },
        [onDragStart],
      );

      const handleDragOver = useCallback(
        (event: DragEndEvent) => {
          if (!isDraggingRef.current) return;
          onDragOver(event);
        },
        [onDragOver],
      );

      const handleDragEnd = useCallback(() => {
        isDraggingRef.current = false;
        setInternalActiveId(null);
        onDragEnd();
      }, [onDragEnd]);

      const handleDragCancel = useCallback(() => {
        isDraggingRef.current = false;
        setInternalActiveId(null);
        onDragEnd();
      }, [onDragEnd]);

      // Set a stable image ID getter function
      const getImageById = useCallback(
        (id: string) => {
          return images.find((item: any) => item.id === id);
        },
        [images],
      );

      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={images} strategy={rectSortingStrategy}>
            <Box
              style={{
                display: 'flex',
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              {images.map((item, i) => (
                <div key={item.id}>
                  <ImageItem
                    key={item.id}
                    idToEdit={idToEdit}
                    isPatching={mediaPatching}
                    edit={() => onEdit(item)}
                    img={item}
                    src={item.attributes.modified_attachment || item.attributes.attachment}
                    idx={i + 1}
                    onClick={handleClick}
                    checkReturn={handleCheck}
                    handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                  />
                </div>
              ))}
            </Box>
            <DragOverlay>
              {internalActiveId ? (
                <div>
                  {
                    <ImageItem
                      idToEdit={idToEdit}
                      isPatching={mediaPatching}
                      idx={images.map((i) => i.id).indexOf(internalActiveId) + 1}
                      img={getImageById(internalActiveId)}
                      src={
                        getImageById(internalActiveId)?.attributes.modified_attachment ||
                        getImageById(internalActiveId)?.attributes.attachment ||
                        ''
                      }
                      handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                    />
                  }
                </div>
              ) : null}
            </DragOverlay>
          </SortableContext>
        </DndContext>
      );
    },
  ),
  propsAreEqual,
);

// Add a display name for better debugging
SortableImages.displayName = 'SortableImages';

export default SortableImages;
